/**
 * Plant Analyser — server-side diagnosis logic.
 * All hint/tile computation lives here, not in component render code.
 */

import { VendorPlant, PlantStatus } from '../types';

// ── Root cause hint ──────────────────────────────────────────────────────────

export interface DiagnosisResult {
  hint: string;
  chipColor: string; // Tailwind bg + text classes
  chipBorder: string;
}

/** Evaluate conditions in priority order and return the first match. */
export function diagnose(p: VendorPlant): DiagnosisResult {
  if (p.hasFaultFlag && p.faultCode) {
    return {
      hint:       `Inverter fault active — ${p.faultCode}`,
      chipColor:  'bg-red-100 text-red-700',
      chipBorder: 'border-red-300',
    };
  }
  if (p.rmsOfflineMinutes !== null && p.rmsOfflineMinutes > 30) {
    return {
      hint:       'RMS offline — check device',
      chipColor:  'bg-pink-100 text-pink-700',
      chipBorder: 'border-pink-300',
    };
  }
  if (p.inverterSilentMinutes !== null && p.inverterSilentMinutes > 30) {
    return {
      hint:       'Inverter silent — check RS485',
      chipColor:  'bg-gray-100 text-gray-600',
      chipBorder: 'border-gray-300',
    };
  }
  if ((p.status === PlantStatus.OFFLINE) && !p.hasFaultFlag) {
    return {
      hint:       'Plant offline / idle',
      chipColor:  'bg-gray-100 text-gray-600',
      chipBorder: 'border-gray-300',
    };
  }
  if (p.ghiBelowNorm) {
    return {
      hint:       'GHI below seasonal norm',
      chipColor:  'bg-blue-100 text-blue-700',
      chipBorder: 'border-blue-300',
    };
  }
  if (!p.hasFaultFlag && !p.ghiBelowNorm && p.prPercent < p.baselinePR90d - 5) {
    return {
      hint:       'Possible soiling — PR below 90-day baseline',
      chipColor:  'bg-amber-100 text-amber-700',
      chipBorder: 'border-amber-300',
    };
  }
  return {
    hint:       'No issues detected',
    chipColor:  'bg-green-100 text-green-700',
    chipBorder: 'border-green-300',
  };
}

// ── Triage tile counts (single pass over full dataset) ───────────────────────

export interface TileCounts {
  prBelow70:         number;
  prDropping7d:      number;
  peerUnderperformer:number;
  lowCUF:            number;
  lowSpecificYield:  number;
  lowAvailability:   number;
  notGenerating:     number;
  activeAlarms:      number;
  rmsOffline:        number;
  inverterNotRsp:    number;
}

export function computeTileCounts(plants: VendorPlant[]): TileCounts {
  const counts: TileCounts = {
    prBelow70: 0, prDropping7d: 0, peerUnderperformer: 0,
    lowCUF: 0, lowSpecificYield: 0, lowAvailability: 0,
    notGenerating: 0, activeAlarms: 0, rmsOffline: 0, inverterNotRsp: 0,
  };

  for (const p of plants) {
    if (p.prPercent < 70) counts.prBelow70++;

    // 7-day declining: every consecutive day lower than the previous
    const t = p.prTrend7d;
    if (t.length >= 7) {
      let declining = true;
      for (let i = 1; i < t.length; i++) {
        if (t[i] >= t[i - 1]) { declining = false; break; }
      }
      if (declining) counts.prDropping7d++;
    }

    if (p.prPercent < p.peerPRAvg - 10) counts.peerUnderperformer++;
    if (p.cufPercent < 15) counts.lowCUF++;
    if (p.specificYieldMonthly < 85) counts.lowSpecificYield++;
    if (p.availabilityPercent < 80) counts.lowAvailability++;

    // Not generating: Offline AND telemetry IS being received (rmsOfflineMinutes null or <30)
    if (p.status === PlantStatus.OFFLINE && (p.rmsOfflineMinutes === null || p.rmsOfflineMinutes <= 30)) {
      counts.notGenerating++;
    }

    if (p.activeAlarms > 0) counts.activeAlarms++;
    if (p.rmsOfflineMinutes !== null && p.rmsOfflineMinutes > 30) counts.rmsOffline++;
    if (p.inverterSilentMinutes !== null && p.inverterSilentMinutes > 30) counts.inverterNotRsp++;
  }

  return counts;
}

// ── Filter predicate per tile ────────────────────────────────────────────────

export type TileKey = keyof TileCounts;

export function tileFilterFn(key: TileKey): (p: VendorPlant) => boolean {
  switch (key) {
    case 'prBelow70':          return p => p.prPercent < 70;
    case 'prDropping7d': return p => {
      const t = p.prTrend7d;
      if (t.length < 7) return false;
      for (let i = 1; i < t.length; i++) { if (t[i] >= t[i - 1]) return false; }
      return true;
    };
    case 'peerUnderperformer': return p => p.prPercent < p.peerPRAvg - 10;
    case 'lowCUF':             return p => p.cufPercent < 15;
    case 'lowSpecificYield':   return p => p.specificYieldMonthly < 85;
    case 'lowAvailability':    return p => p.availabilityPercent < 80;
    case 'notGenerating':      return p => p.status === PlantStatus.OFFLINE && (p.rmsOfflineMinutes === null || p.rmsOfflineMinutes <= 30);
    case 'activeAlarms':       return p => p.activeAlarms > 0;
    case 'rmsOffline':         return p => p.rmsOfflineMinutes !== null && p.rmsOfflineMinutes > 30;
    case 'inverterNotRsp':     return p => p.inverterSilentMinutes !== null && p.inverterSilentMinutes > 30;
  }
}
